<template>
  <agent-default>
    <div class="tw-w-full">
      <div class="tw-px-4 md:tw-px-8 tw-pt-6 tw-pb-10">

        <!-- Page Header -->
        <header class="page-header tw-mb-6">
          <div>
            <h2 class="page-title">Transactions</h2>
            <p class="page-subtitle">View your transaction history and activity.</p>
          </div>
        </header>

        <!-- Summary Totals Strip -->
        <div class="totals-strip tw-mb-6" v-if="!loading && transactions.length > 0">
          <div class="total-pill">
            <span class="total-pill__label">Total Transactions</span>
            <span class="total-pill__value">{{ transactions.length }}</span>
          </div>
        </div>

        <!-- Filters Row -->
        <div class="filters-card tw-mb-5">
          <div class="filter-group">
            <label class="filter-label">Search by Policy</label>
            <div class="policy-search-row">
              <input
                v-model="searchByPolicy"
                type="text"
                placeholder="Enter policy or claim number..."
                class="policy-input"
                @keyup.enter="searchByPolicyApply"
              />
              <button class="btn-apply" @click="searchByPolicyApply" :disabled="loading">
                <v-icon v-if="loading" size="16" class="tw-mr-1" color="white">mdi-loading</v-icon>
                <v-icon v-else size="16" class="tw-mr-1" color="white">mdi-magnify</v-icon>
                Search
              </button>

              <button
                v-if="filteredTransactions.length > 0"
                class="btn-export-pdf tw-ml-2"
                @click="exportToPdf"
                :disabled="loading"
              >
                <v-icon size="16" class="tw-mr-1" color="white">mdi-file-pdf-box</v-icon>
                Export to PDF
              </button>

              <!-- Excel Export -->
              <vue-json-to-csv
                v-if="filteredTransactions.length > 0"
                :json-data="filteredTransactions"
                :csv-title="`Transactions_Report_${new Date().toISOString().slice(0, 10)}`"
                :labels="{
                  transDate: { title: 'Date' },
                  ourRef: { title: 'Our Ref' },
                  insured: { title: 'Insured' },
                  type: { title: 'Type' },
                  policyOrClaimNo: { title: 'Policy/Claim No' },
                  coverFrom: { title: 'Cover From' },
                  coverTo: { title: 'Cover To' }
                }"
              >
                <button class="btn-export-excel tw-ml-2" :disabled="loading">
                  <v-icon size="16" class="tw-mr-1" color="white">mdi-file-excel-box</v-icon>
                  Export to Excel
                </button>
              </vue-json-to-csv>
            </div>
          </div>
          <div class="search-wrapper">
            <v-icon class="search-icon" color="#94A3B8" size="20">mdi-magnify</v-icon>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Filter by reference, insured..."
              class="search-input"
            />
            <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''">
              <v-icon size="16" color="#94A3B8">mdi-close</v-icon>
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="transactions-loading">

          <!-- Summary Total Shimmer -->
          <div class="totals-strip tw-mb-6">
            <div class="transaction-total-shimmer">
              <div class="shimmer shimmer-total-label"></div>
              <div class="shimmer shimmer-total-value"></div>
            </div>
          </div>

          <!-- Transaction Table Shimmer -->
          <div class="table-card transaction-shimmer-card">
            <div class="transaction-shimmer-table">

              <!-- Header -->
              <div class="transaction-shimmer-header">
                <div class="shimmer shimmer-header-date"></div>
                <div class="shimmer shimmer-header-ref"></div>
                <div class="shimmer shimmer-header-insured"></div>
                <div class="shimmer shimmer-header-type"></div>
                <div class="shimmer shimmer-header-policy"></div>
                <div class="shimmer shimmer-header-cover"></div>
                <div class="shimmer shimmer-header-cover"></div>
                <div class="shimmer shimmer-header-action"></div>
              </div>

              <!-- Rows -->
              <div
                v-for="n in 6"
                :key="`transaction-shimmer-${n}`"
                class="transaction-shimmer-row"
              >
                <div class="shimmer shimmer-date"></div>

                <div class="shimmer shimmer-ref"></div>

                <div class="shimmer shimmer-insured"></div>

                <div class="shimmer shimmer-type"></div>

                <div class="shimmer shimmer-policy"></div>

                <div class="shimmer shimmer-cover"></div>

                <div class="shimmer shimmer-cover"></div>

                <div class="shimmer shimmer-action"></div>
              </div>

            </div>
          </div>

        </div>

        <!-- Table -->
        <div v-else class="table-card">
          <div class="table-scroll" v-if="filteredTransactions.length > 0">
            <table class="comm-table">
              <thead>
                <tr>
                  <th>Trans Date</th>
                  <th>Our Ref</th>
                  <th>Insured</th>
                  <th>Type</th>
                  <th>Policy/Claim No</th>
                  <th>Cover From</th>
                  <th>Cover To</th>
                  <th class="tw-text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in filteredTransactions"
                  :key="row.id || row.ourRef || i"
                  class="comm-row clickable-row"
                  @click="viewTransactionDetails(row)"
                >
                  <td class="cell-muted">{{ row.transDate || '—' }}</td>
                  <td><span class="mono-text">{{ row.ourRef || '—' }}</span></td>
                  <td><span class="client-name">{{ row.insured || '—' }}</span></td>
                  <td><span class="mono-text">{{ row.type || '—' }}</span></td>
                  <td><span class="mono-text">{{ row.policyOrClaimNo || '—' }}</span></td>
                  <td class="cell-muted">{{ row.coverFrom || '—' }}</td>
                  <td class="cell-muted">{{ row.coverTo || '—' }}</td>
                  <td class="tw-text-center" @click.stop>
                    <v-btn small color="primary" @click="viewTransactionDetails(row)">
                      View Details
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-else class="empty-state">
            <div class="empty-icon-wrap">
              <v-icon color="#00AEEF" size="40">mdi-swap-horizontal</v-icon>
            </div>
            <p class="empty-title">No transactions found</p>
            <p class="empty-subtitle">Try adjusting your search or check back later.</p>
          </div>
        </div>

      </div>
    </div>
  </agent-default>
</template>

<script>
import AgentDefault from '@/components/layout/AgentDefault';
import axios from 'axios';
import VueJsonToCsv from 'vue-json-to-csv';
import { trackAgentInteraction } from '@/utils/agentAnalytics';

export default {
  name: 'AgentTransactions',
  components: { AgentDefault, VueJsonToCsv },
  data() {
    return {
      loading: true,
      searchQuery: '',
      searchByPolicy: '',
      transactions: [],
    };
  },
  computed: {
    filteredTransactions() {
      if (!this.searchQuery || !this.searchQuery.trim()) return this.transactions;
      const q = this.searchQuery.trim().toLowerCase();
      return this.transactions.filter((row) => {
        const ref = (row.ourRef || '').toLowerCase();
        const insured = (row.insured || '').toLowerCase();
        const policy = (row.policyOrClaimNo || '').toLowerCase();
        const type = (row.type || '').toLowerCase();
        return ref.includes(q) || insured.includes(q) || policy.includes(q) || type.includes(q);
      });
    },
  },
  mounted() {
    this.fetchTransactions();
  },
  methods: {
    formatCurrency(value) {
      if (value == null || value === '') return '—';
      const num = Number(value);
      if (Number.isNaN(num)) return value;
      return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 }).format(num);
    },
    async getAgentCode() {
      const user = this.$store?.getters?.['auth/authenticatedUser'];
      const email = user?.email;
      const cacheKey = email ? `agentCodeTransactions_${email}` : 'agentCodeTransactions';
      const cached = localStorage.getItem(cacheKey);
      if (cached) return cached;
      const cachedNumeric = localStorage.getItem('agentNumericId');
      if (cachedNumeric) {
        localStorage.setItem(cacheKey, cachedNumeric);
        return cachedNumeric;
      }
      const agnCodeParam = user?.['custom:agent_code'];
      if (!agnCodeParam && !email) return null;
      try {
        const baseUrl = '/customer/gis/agent/dashboard/summary';
        const url = agnCodeParam
          ? `${baseUrl}?agn_code=${agnCodeParam}`
          : `${baseUrl}?email=${encodeURIComponent(email)}`;
        const res = await axios.get(url, { validateStatus: () => true, _skipErrorRedirect: true });
        if (res.status === 200 && res.data) {
          const code = res.data?.agent?.code;
          if (code != null) {
            const val = String(code);
            localStorage.setItem(cacheKey, val);
            return val;
          }
        }
      } catch {
        // fallback
      }
      return agnCodeParam ? String(agnCodeParam) : null;
    },
    async fetchTransactions() {
      this.loading = true;
      try {
        const agnCode = await this.getAgentCode();
        if (!agnCode) {
          this.transactions = [];
          return;
        }

        const params = { agnCode };
        const policyNo = (this.searchByPolicy || '').trim();
        if (policyNo) params.policyOrClaimNo = policyNo;

        const res = await axios.get('/customer/gis/agent/transactions', {
          params,
          validateStatus: () => true,
          _skipErrorRedirect: true,
        });

        if (res.status === 200 && res.data) {
          const { data } = res;
          const obj = data.object ?? data;
          let raw = [];
          if (Array.isArray(data.data)) raw = data.data;
          else if (Array.isArray(data)) raw = data;
          else if (Array.isArray(obj?.data)) raw = obj.data;
          else if (Array.isArray(obj?.pageItems)) raw = obj.pageItems;
          else if (Array.isArray(obj)) raw = obj;

          this.transactions = raw.map(this.normalizeRow);
        } else {
          this.transactions = [];
        }
      } catch (err) {
        if (this.$notify?.fromError) this.$notify.fromError(err, 'Fetch Failed');
        this.transactions = [];
      } finally {
        this.loading = false;
      }
    },
    normalizeRow(row) {
      return {
        ...row,
        id: row.id,
        transDate: row.transDate ?? row.transactionDate ?? row.date ?? row.createdAt ?? '',
        ourRef: row.ourRef ?? row.our_ref ?? row.ref ?? row.reference ?? '',
        insured: row.insured ?? row.insuredName ?? row.clientName ?? row.client ?? '',
        type: row.type ?? row.transactionType ?? row.category ?? '',
        policyOrClaimNo: row.policyOrClaimNo ?? row.policyOrClaim ?? row.policyNo ?? row.claimNo ?? row.policy ?? '',
        coverFrom: row.coverFrom ?? row.cover_from ?? row.coverFromDate ?? '',
        coverTo: row.coverTo ?? row.cover_to ?? row.coverToDate ?? '',
      };
    },
    searchByPolicyApply() {
      trackAgentInteraction(this, 'transactions_search_by_policy', {
        query: (this.searchByPolicy || '').trim() || 'empty',
      });
      this.fetchTransactions();
    },
    viewTransactionDetails(txn) {
      trackAgentInteraction(this, 'transactions_view_details', {
        transaction_ref: txn?.ourRef || txn?.id || 'unknown',
      });
      const ref = String(txn.ourRef || txn.id || `txn_${Date.now()}`);
      const key = `agentTransaction_${ref}`;
      try {
        sessionStorage.setItem(key, JSON.stringify(txn));
      } catch {
        // ignore storage errors
      }
      this.$router.push({
        name: 'AgentTransactionDetails',
        params: { transactionId: ref },
        query: { insured: txn.insured },
      });
    },
    exportToPdf() {
      trackAgentInteraction(this, 'transactions_export_pdf', {
        rows: this.filteredTransactions.length,
      });
      try {
        const { jsPDF: JsPDF } = window.jspdf;
        const doc = new JsPDF('l', 'mm', 'a4');
        const pageW = doc.internal.pageSize.getWidth();
        const margin = 14;

        // Branding Header
        const logoY = 10;
        doc.addImage('/pacis-logo-tagline.png', 'PNG', margin, logoY, 38, 14);

        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.setFont('helvetica', 'normal');
        doc.text('Pacis Centre, 4th Floor, off Waiyaki Way', pageW - margin, logoY + 4, { align: 'right' });
        doc.text('P.O BOX 1870-00200, Nairobi, Kenya', pageW - margin, logoY + 8, { align: 'right' });
        doc.text('Email: info@paciskenya.com', pageW - margin, logoY + 12, { align: 'right' });

        // Divider
        doc.setDrawColor(230, 235, 245);
        doc.setLineWidth(0.5);
        doc.line(margin, 28, pageW - margin, 28);

        // Title
        doc.setFontSize(16);
        doc.setTextColor(30, 66, 118); // #1E4276
        doc.setFont('helvetica', 'bold');
        doc.text('TRANSACTIONS REPORT', margin, 40);

        // Report Info
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, 46);
        if (this.searchByPolicy) {
          doc.text(`Search filter: ${this.searchByPolicy}`, margin, 51);
        }

        const head = [['Trans Date', 'Our Ref', 'Insured', 'Type', 'Policy/Claim No', 'Cover From', 'Cover To']];
        const body = this.filteredTransactions.map((t) => [
          t.transDate,
          t.ourRef,
          t.insured,
          t.type,
          t.policyOrClaimNo,
          t.coverFrom,
          t.coverTo,
        ]);

        const tableTop = 58;
        if (typeof doc.autoTable === 'function') {
          doc.autoTable({
            startY: tableTop,
            head,
            body,
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: [30, 66, 118], fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            margin: { left: margin, right: margin },
          });
        } else if (window.jspdf && typeof window.jspdf.autoTable === 'function') {
          window.jspdf.autoTable(doc, {
            startY: tableTop,
            head,
            body,
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: [30, 66, 118], fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            margin: { left: margin, right: margin },
          });
        }

        // Footer note
        const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : tableTop + 20;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150);
        doc.text('This is a computer-generated report and does not require a signature.', margin, finalY);

        doc.save(`Transactions_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
      } catch (err) {
        console.error('PDF Export Error:', err);
        if (this.$notify?.fromError) this.$notify.fromError(err, 'Export Failed');
      }
    },
  },
  metaInfo: { title: 'Transactions' },
};
</script>

<style scoped>
/* ── Header ── */
.page-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0 0 4px 0;
}
.page-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}

/* ── Totals Strip ── */
.totals-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.total-pill {
  background: white;
  border: 1px solid #E8EDF2;
  border-radius: 12px;
  padding: 14px 20px;
  display: flex;
  flex-direction: column;
  min-width: 160px;
  flex: 1;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.total-pill__label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.total-pill__value {
  font-size: 1.15rem;
  font-weight: 800;
  color: #1E4276;
}

/* ── Filters Card ── */
.filters-card {
  background: white;
  border: 1px solid #E8EDF2;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.policy-search-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.policy-search-row .search-wrapper {
  min-width: 260px;
  flex: 1;
}

.btn-apply {
  background: #00AEEF;
  color: white;
  border: none;
  padding: 0 20px;
  height: 44px;
  border-radius: 9px;
  font-weight: 700;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: opacity 0.2s;
  box-shadow: 0 2px 8px rgba(0, 174, 239, 0.25);
  white-space: nowrap;
}

.btn-apply:hover:not(:disabled) { opacity: 0.9; }
.btn-apply:disabled { opacity: 0.6; cursor: not-allowed; }

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.policy-search-row {
  display: flex;
  gap: 12px;
  align-items: center;
}

.policy-input {
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 9px;
  padding: 0 14px;
  height: 44px;
  font-size: 0.9rem;
  color: #1E4276;
  min-width: 240px;
  transition: border-color 0.2s;
}

.policy-input:focus {
  outline: none;
  border-color: #00AEEF;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.12);
}

.btn-apply {
  background: #00AEEF;
  color: white;
  border: none;
  padding: 0 20px;
  height: 44px;
  border-radius: 9px;
  font-weight: 700;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: opacity 0.2s;
  box-shadow: 0 2px 8px rgba(0, 174, 239, 0.25);
  white-space: nowrap;
}

.btn-apply:hover:not(:disabled) { opacity: 0.9; }
.btn-apply:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-export-pdf {
  background: #E53E3E;
  color: white;
  border: none;
  padding: 0 20px;
  height: 44px;
  border-radius: 9px;
  font-weight: 700;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: opacity 0.2s;
  box-shadow: 0 2px 8px rgba(229, 62, 62, 0.25);
  white-space: nowrap;
}
.btn-export-pdf:hover:not(:disabled) { opacity: 0.9; }
.btn-export-pdf:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-export-excel {
  background: #10B981;
  color: white;
  border: border;
  padding: 0 20px;
  height: 44px;
  border-radius: 9px;
  font-weight: 700;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: opacity 0.2s;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);
  white-space: nowrap;
}
.btn-export-excel:hover:not(:disabled) { opacity: 0.9; }
.btn-export-excel:disabled { opacity: 0.6; cursor: not-allowed; }

.search-wrapper {
  display: flex;
  align-items: center;
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 9px;
  padding: 0 12px;
  height: 44px;
  min-width: 240px;
  flex: 1;
  max-width: 400px;
  gap: 8px;
  transition: border-color 0.2s;
}

.search-wrapper:focus-within {
  border-color: #00AEEF;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.12);
  background: white;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  color: #1E4276;
}

.search-input::placeholder { color: #94A3B8; }

.search-clear {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
}

/* ── Table Card ── */
.table-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #E8EDF2;
  box-shadow: 0 4px 24px rgba(0,0,0,0.06);
  overflow: hidden;
}

.table-scroll {
  overflow-x: auto;
}

.comm-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 960px;
}

.comm-table thead tr {
  background: #F8FAFC;
  border-bottom: 2px solid #E8EDF2;
}

.comm-table th {
  text-align: left;
  padding: 14px 16px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.comm-row {
  border-bottom: 1px solid #F1F5F9;
  transition: background 0.15s;
}

.comm-row:last-child { border-bottom: none; }
.comm-row:hover { background: #F0F9FF; }
.comm-row.clickable-row { cursor: pointer; }

.comm-table td {
  padding: 14px 16px;
  vertical-align: middle;
  font-size: 0.875rem;
}

.client-name {
  font-weight: 700;
  color: #1E4276;
}

.mono-text {
  font-size: 0.82rem;
  color: #64748B;
  font-family: monospace;
  background: #F1F5F9;
  padding: 2px 7px;
  border-radius: 5px;
}

.cell-muted { color: #94A3B8; font-size: 0.82rem; }

.commission-value { color: #00AEEF; font-weight: 700; }
.tax-value { color: #EF4444; font-weight: 600; }

.status-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 9999px;
  display: inline-block;
}

.status-success {
  background: #ECFDF5;
  color: #059669;
}

.status-pending {
  background: #FFFBEB;
  color: #D97706;
}

/* ── Totals Footer ── */
.totals-row {
  background: #1E4276;
}

.totals-row td {
  padding: 16px;
}

.totals-label {
  font-weight: 800;
  color: rgba(255,255,255,0.7);
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: right;
}

.totals-cell {
  font-weight: 800;
  color: white;
  font-size: 0.9rem;
}

.totals-cell--net {
  color: #FFD700;
}

/* ── Empty State ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}

.empty-icon-wrap {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: #EBF8FF;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0 0 6px;
}

.empty-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}
/* ── Transactions Shimmer Loading ── */

.transactions-loading {
  width: 100%;
}

/* Base shimmer */

.shimmer {
  position: relative;
  overflow: hidden;
  background: #E9EEF3;
  border-radius: 6px;
}

.shimmer::after {
  content: '';
  position: absolute;
  top: 0;
  left: -150%;
  width: 150%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.65) 50%,
    transparent 100%
  );
  animation: transactions-shimmer 1.5s infinite;
}

@keyframes transactions-shimmer {
  0% {
    left: -150%;
  }

  100% {
    left: 150%;
  }
}

/* ── Total Transactions ── */

.transaction-total-shimmer {
  background: white;
  border: 1px solid #E8EDF2;
  border-radius: 12px;
  padding: 14px 20px;
  min-width: 160px;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.shimmer-total-label {
  width: 125px;
  height: 11px;
  margin-bottom: 9px;
}

.shimmer-total-value {
  width: 45px;
  height: 19px;
}

/* ── Transaction Table ── */

.transaction-shimmer-card {
  overflow: hidden;
}

.transaction-shimmer-table {
  width: 100%;
  overflow-x: auto;
}

.transaction-shimmer-header,
.transaction-shimmer-row {
  display: grid;
  grid-template-columns:
    minmax(100px, 0.9fr)
    minmax(110px, 1fr)
    minmax(150px, 1.4fr)
    minmax(90px, 0.8fr)
    minmax(140px, 1.2fr)
    minmax(100px, 0.9fr)
    minmax(100px, 0.9fr)
    minmax(110px, 0.9fr);
  align-items: center;
  column-gap: 16px;
  min-width: 960px;
  padding-left: 16px;
  padding-right: 16px;
}

.transaction-shimmer-header {
  min-height: 48px;
  background: #F8FAFC;
  border-bottom: 2px solid #E8EDF2;
}

.transaction-shimmer-row {
  min-height: 64px;
  border-bottom: 1px solid #F1F5F9;
}

/* ── Header Placeholders ── */

.shimmer-header-date {
  width: 65px;
  height: 11px;
}

.shimmer-header-ref {
  width: 60px;
  height: 11px;
}

.shimmer-header-insured {
  width: 60px;
  height: 11px;
}

.shimmer-header-type {
  width: 40px;
  height: 11px;
}

.shimmer-header-policy {
  width: 105px;
  height: 11px;
}

.shimmer-header-cover {
  width: 70px;
  height: 11px;
}

.shimmer-header-action {
  width: 50px;
  height: 11px;
  margin: auto;
}

/* ── Row Placeholders ── */

.shimmer-date {
  width: 75px;
  height: 14px;
}

.shimmer-ref {
  width: 85px;
  height: 22px;
  border-radius: 5px;
}

.shimmer-insured {
  width: 125px;
  height: 15px;
}

.shimmer-type {
  width: 75px;
  height: 22px;
  border-radius: 5px;
}

.shimmer-policy {
  width: 110px;
  height: 22px;
  border-radius: 5px;
}

.shimmer-cover {
  width: 75px;
  height: 14px;
}

.shimmer-action {
  width: 90px;
  height: 32px;
  border-radius: 6px;
  margin: auto;
}
</style>
